import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coffee, TrendingUp, Users, Zap, DollarSign, Activity } from "lucide-react";
import { superApi, getSuperPin } from "../lib/admin";
import { apiRaw } from "../lib/api";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Pitch — big-screen live-updating demo dashboard.
 * Not linked from anywhere in the UI. Auth gate: super-admin PIN via localStorage.
 * Auto-refreshes every 5s.
 */

const useLiveStats = () => {
    const [data, setData] = useState({ stats: null, network: null, tenants: [], latestOrders: [] });

    useEffect(() => {
        let stopped = false;
        const load = async () => {
            try {
                const [s, n, t] = await Promise.all([
                    superApi.get("/super/stats"),
                    superApi.get("/super/network"),
                    superApi.get("/super/tenants"),
                ]);
                if (stopped) return;
                // For each active tenant grab latest 3 orders (via public menu path won't work — use staff route which is open for tenant-scoped)
                const latest = [];
                for (const tn of (t.data.tenants || []).filter((x) => x.status === "active").slice(0, 6)) {
                    try {
                        const r = await apiRaw.get(`/t/${tn.slug}/staff/orders`);
                        for (const o of (r.data.orders || []).slice(0, 2)) {
                            latest.push({ ...o, _cafe: tn.name, _slug: tn.slug });
                        }
                    } catch {}
                }
                latest.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                if (stopped) return;
                setData({
                    stats: s.data,
                    network: n.data,
                    tenants: t.data.tenants || [],
                    latestOrders: latest.slice(0, 8),
                });
            } catch {
                /* ignore transient */
            }
        };
        load();
        const int = setInterval(load, 5000);
        return () => {
            stopped = true;
            clearInterval(int);
        };
    }, []);

    return data;
};

const Ticker = ({ value }) => {
    // Simple animated number without heavy dependency
    const [display, setDisplay] = useState(value);
    useEffect(() => setDisplay(value), [value]);
    return (
        <motion.span key={display} initial={{ opacity: 0.5, y: 4 }} animate={{ opacity: 1, y: 0 }}>
            {display}
        </motion.span>
    );
};

/**
 * useDemoOrders — visual-only fake order stream for pitch safety net.
 * Enabled via ?demo=1 query param. Never writes to backend.
 * Spawns 1-2 synthetic orders per minute (35-55s jitter).
 */
const DEMO_CAFES = [
    { name: "Blue Bottle Newtown", slug: "bluebottle" },
    { name: "Brew Bar Fitzroy", slug: "brewbar" },
    { name: "South Cross Coffee", slug: "southcross" },
    { name: "Market Lane", slug: "marketlane" },
    { name: "Padre Coffee", slug: "padre" },
    { name: "Seven Seeds", slug: "sevenseeds" },
];
const DEMO_ITEM_SETS = [
    [{ name: "Flat White · L" }, { name: "Almond Croissant" }],
    [{ name: "Long Black · M" }],
    [{ name: "Oat Latte · L" }, { name: "Banana Bread" }],
    [{ name: "Cappuccino · M" }],
    [{ name: "Piccolo" }, { name: "Blueberry Muffin" }],
    [{ name: "Iced Latte · L" }],
    [{ name: "Mocha · L" }, { name: "Ham & Cheese Toastie" }],
    [{ name: "Cold Brew" }, { name: "Protein Ball" }],
];
const DEMO_STATUSES = ["received", "ready", "completed", "received"];

const useDemoOrders = (enabled) => {
    const [demo, setDemo] = useState([]);
    useEffect(() => {
        if (!enabled) {
            setDemo([]);
            return;
        }
        const spawn = () => {
            const c = DEMO_CAFES[Math.floor(Math.random() * DEMO_CAFES.length)];
            const items = DEMO_ITEM_SETS[Math.floor(Math.random() * DEMO_ITEM_SETS.length)];
            const status = DEMO_STATUSES[Math.floor(Math.random() * DEMO_STATUSES.length)];
            const n = Math.floor(Math.random() * 9000) + 1000;
            const order = {
                _cafe: c.name,
                _slug: c.slug,
                order_number: `${c.slug}-D${n}`,
                status,
                total: 4.5 + Math.random() * 11.5,
                items,
                created_at: new Date().toISOString(),
                _demo: true,
            };
            setDemo((prev) => [order, ...prev].slice(0, 8));
        };
        // Seed 2 orders immediately so the panel isn't empty
        spawn();
        setTimeout(spawn, 900);
        let timer;
        const loop = () => {
            const jitter = 35000 + Math.random() * 20000; // 35-55s
            timer = setTimeout(() => {
                spawn();
                loop();
            }, jitter);
        };
        loop();
        return () => clearTimeout(timer);
    }, [enabled]);
    return demo;
};

const Pitch = () => {
    const nav = useNavigate();
    const location = useLocation();
    const demoMode = new URLSearchParams(location.search).get("demo") === "1";
    const { stats, network, tenants, latestOrders } = useLiveStats();
    const demoOrders = useDemoOrders(demoMode);

    if (!getSuperPin()) {
        return (
            <div className="min-h-screen bg-[#0E0906] text-white flex flex-col items-center justify-center p-8 text-center">
                <div className="font-display font-black text-4xl">Super-admin only</div>
                <p className="mt-3 text-white/60 max-w-sm">
                    Sign in at <span className="font-mono">/super-admin</span> first, then come back
                    here.
                </p>
                <button
                    onClick={() => nav("/super-admin")}
                    className="mt-8 rounded-full bg-[#FF4F18] text-white px-8 py-4 font-display font-bold active:scale-95"
                >
                    Go to super-admin
                </button>
            </div>
        );
    }

    const activeCafes = tenants.filter((t) => t.status === "active");
    const activeCount = activeCafes.length;
    const totalRevenue = stats?.total_gmv ?? 0;
    const totalOrders = stats?.total_orders ?? 0;
    const consumers = network?.total_consumers ?? 0;
    const powerUsers = network?.power_users ?? 0;
    const orders7d = stats?.orders_7d ?? 0;

    // Merge demo orders on top of live orders when demo mode is on.
    const feedOrders = demoMode
        ? [...demoOrders, ...latestOrders].slice(0, 8)
        : latestOrders;

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0E0906] via-[#2C1A12] to-[#0E0906] text-white overflow-hidden">
            {/* Ambient grain */}
            <div
                className="pointer-events-none fixed inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
            />

            <header className="relative z-10 flex items-center justify-between px-10 py-6 border-b border-white/5">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#FF4F18]">
                        Live · Coffeein7 Platform
                    </div>
                    <div className="mt-1 font-display font-black text-3xl leading-none">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-full bg-white/5 border border-white/10 px-4 py-2" data-testid="pitch-live-badge">
                    <span className={`h-2.5 w-2.5 rounded-full animate-pulse ${demoMode ? "bg-[#FF4F18]" : "bg-[#1B995E]"}`} />
                    <span className="text-xs uppercase tracking-[0.25em] font-bold">
                        {demoMode ? "Demo mode · simulated feed" : "Live · updated every 5s"}
                    </span>
                </div>
            </header>

            {/* Hero stats */}
            <div className="relative z-10 px-10 mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
                <BigStat
                    icon={Coffee}
                    label="Cafés live"
                    value={activeCount}
                    testid="pitch-cafes"
                    accent="#FF4F18"
                />
                <BigStat
                    icon={Users}
                    label="Consumers"
                    value={consumers.toLocaleString()}
                    sub={network ? `${network.avg_cafes_per_consumer} cafés avg` : ""}
                    testid="pitch-consumers"
                />
                <BigStat
                    icon={Zap}
                    label="Cross-café power users"
                    value={powerUsers.toLocaleString()}
                    sub={network ? `${network.power_user_pct}% · 2+ cafés` : ""}
                    testid="pitch-power-users"
                    accent="#FF4F18"
                />
                <BigStat
                    icon={DollarSign}
                    label="Platform GMV"
                    value={`$${totalRevenue.toLocaleString()}`}
                    sub={`${orders7d} orders in 7d`}
                    testid="pitch-gmv"
                />
            </div>

            {/* Cafés grid + live orders */}
            <div className="relative z-10 px-10 mt-10 pb-10 grid md:grid-cols-[1fr_360px] gap-8">
                {/* Café tiles */}
                <div>
                    <div className="text-xs uppercase tracking-[0.35em] font-bold text-white/50 mb-4 flex items-center gap-2">
                        <Activity className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Cafés on the network
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {activeCafes.map((t) => (
                            <div
                                key={t.slug}
                                className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur"
                                data-testid={`pitch-cafe-${t.slug}`}
                            >
                                <div className="font-display font-black text-lg leading-tight truncate">
                                    {t.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-mono mt-1">
                                    /{t.slug}
                                </div>
                                <div className="mt-3 flex items-baseline justify-between">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/50">
                                            Orders
                                        </div>
                                        <div className="font-display font-black text-2xl">
                                            {t.stats?.orders ?? 0}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/50">
                                            GMV
                                        </div>
                                        <div className="font-display font-bold text-lg text-[#FF4F18]">
                                            ${(t.stats?.revenue ?? 0).toFixed(0)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {activeCafes.length === 0 && (
                            <div className="col-span-full text-white/40 text-sm">
                                No active cafés yet.
                            </div>
                        )}
                    </div>

                    {/* Bottom hero line */}
                    <div className="mt-10 rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            Traction
                        </div>
                        <div className="mt-2 font-display font-black text-3xl leading-tight">
                            <Ticker value={totalOrders.toLocaleString()} /> orders processed. <Ticker value={consumers.toLocaleString()} /> Australians on the network.
                        </div>
                    </div>
                </div>

                {/* Live orders feed */}
                <div>
                    <div className="text-xs uppercase tracking-[0.35em] font-bold text-white/50 mb-4 flex items-center gap-2">
                        <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Latest orders
                    </div>
                    <div className="space-y-2" data-testid="pitch-live-orders">
                        <AnimatePresence>
                            {feedOrders.map((o, i) => (
                                <motion.div
                                    key={`${o._slug}-${o.order_number}`}
                                    initial={{ opacity: 0, x: 30, scale: 0.95 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: -30 }}
                                    transition={{ delay: i * 0.02 }}
                                    className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 backdrop-blur"
                                    data-testid={o._demo ? "pitch-demo-order" : "pitch-live-order"}
                                >
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className="text-xs font-display font-bold truncate">
                                            {o._cafe}
                                        </div>
                                        <div className="text-[10px] font-mono text-white/40 shrink-0">
                                            #{o.order_number?.split("-").pop()}
                                        </div>
                                    </div>
                                    <div className="mt-1 text-[11px] text-white/60 truncate">
                                        {(o.items || []).slice(0, 2).map((i) => i.name).join(", ")}
                                        {(o.items || []).length > 2 && " +…"}
                                    </div>
                                    <div className="mt-1 flex items-center justify-between">
                                        <div
                                            className={`text-[9px] uppercase tracking-[0.2em] font-bold ${
                                                o.status === "ready"
                                                    ? "text-[#1B995E]"
                                                    : o.status === "completed"
                                                    ? "text-white/40"
                                                    : "text-[#FF4F18]"
                                            }`}
                                        >
                                            · {o.status}
                                        </div>
                                        <div className="text-xs font-display font-bold text-[#FF4F18]">
                                            ${(o.total ?? 0).toFixed(2)}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                        {feedOrders.length === 0 && (
                            <div className="text-white/30 text-sm text-center py-8">
                                Waiting for orders…
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const BigStat = ({ icon: Icon, label, value, sub, accent, testid }) => (
    <div
        className={`rounded-3xl backdrop-blur border p-6 ${
            accent
                ? "bg-[#FF4F18]/10 border-[#FF4F18]/30"
                : "bg-white/5 border-white/10"
        }`}
        data-testid={testid}
    >
        <div className="flex items-center gap-2">
            <Icon
                className={`h-4 w-4 ${accent ? "text-[#FF4F18]" : "text-white/50"}`}
                strokeWidth={2.5}
            />
            <div
                className={`text-[10px] uppercase tracking-[0.3em] font-bold ${
                    accent ? "text-[#FF4F18]" : "text-white/50"
                }`}
            >
                {label}
            </div>
        </div>
        <div className="mt-3 font-display font-black text-4xl md:text-5xl leading-none">
            <Ticker value={value} />
        </div>
        {sub && <div className="mt-2 text-xs text-white/50">{sub}</div>}
    </div>
);

export default Pitch;
